import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  width: 700px;
  max-width: 90%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const ServerUrl = styled.div`
  background: #f6f8fa;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #d1d5da;
  font-family: monospace;
  font-size: 14px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CopyButton = styled.button`
  padding: 6px 12px;
  background: #0969da;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #0860ca;
  }
`;

const EndpointList = styled.div`
  margin-top: 16px;
`;

const Endpoint = styled.div`
  background: #f6f8fa;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 8px;
  border-left: 3px solid ${props => 
    props.method === 'GET' ? '#28a745' :
    props.method === 'POST' ? '#0969da' :
    props.method === 'PUT' ? '#f59e0b' :
    props.method === 'DELETE' ? '#d1242f' : '#586069'
  };
`;

const Method = styled.span`
  font-weight: 600;
  color: ${props => 
    props.method === 'GET' ? '#28a745' :
    props.method === 'POST' ? '#0969da' :
    props.method === 'PUT' ? '#f59e0b' :
    props.method === 'DELETE' ? '#d1242f' : '#586069'
  };
  margin-right: 8px;
`;

const Path = styled.span`
  font-family: monospace;
  font-size: 13px;
`;

const CloseButton = styled.button`
  padding: 10px 20px;
  background: #f6f8fa;
  color: #24292f;
  border: 1px solid #d1d5da;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    background: #e1e4e8;
  }
`;

const Footer = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e1e4e8;
  display: flex;
  justify-content: flex-end;
`;

const Info = styled.div`
  background: #e7f3ff;
  border: 1px solid #0969da;
  border-radius: 6px;
  padding: 12px;
  font-size: 13px;
  color: #0969da;
  margin-bottom: 16px;
`;

function MockServerModal({ spec, onClose }) {
  const [endpoints, setEndpoints] = useState([]);
  const mockServerUrl = 'https://mockapi.io/api/v1';

  useEffect(() => {
    try {
      const parsed = yaml.load(spec);
      const paths = parsed.paths || {};
      const endpointList = [];
      
      Object.keys(paths).forEach(path => {
        Object.keys(paths[path]).forEach(method => {
          if (['get', 'post', 'put', 'delete', 'patch'].includes(method)) {
            endpointList.push({
              method: method.toUpperCase(),
              path: path,
              summary: paths[path][method].summary || ''
            });
          }
        });
      });
      
      setEndpoints(endpointList);
    } catch (e) {
      console.error('Error parsing spec:', e);
    }
  }, [spec]);

  const copyUrl = () => {
    navigator.clipboard.writeText(mockServerUrl);
    alert('Mock server URL copied!');
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>🚀 Mock Server</Title>
        </Header>
        <Body>
          <Info>
            ℹ️ This is a simulated mock server. In production, use tools like Prism, Mockoon, or Stoplight to create real mock servers.
          </Info>
          
          <ServerUrl>
            <span>{mockServerUrl}</span>
            <CopyButton onClick={copyUrl}>Copy</CopyButton>
          </ServerUrl>
          
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>Available Endpoints ({endpoints.length})</h4>
          <EndpointList>
            {endpoints.map((endpoint, index) => (
              <Endpoint key={index} method={endpoint.method}>
                <Method method={endpoint.method}>{endpoint.method}</Method>
                <Path>{endpoint.path}</Path>
                {endpoint.summary && (
                  <div style={{ fontSize: '12px', color: '#586069', marginTop: '4px' }}>
                    {endpoint.summary}
                  </div>
                )}
              </Endpoint>
            ))}
          </EndpointList>
          
          {endpoints.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#586069' }}>
              No endpoints defined in spec
            </div>
          )}
        </Body>
        <Footer>
          <CloseButton onClick={onClose}>Close</CloseButton>
        </Footer>
      </Modal>
    </Overlay>
  );
}

export default MockServerModal;
